import React, { useState, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { svgColorFadeIn } from '../../lib/styles';
import { media } from '../../lib/styles';
import { avatars, AvatarSizing, boxShadows, colors } from '../../lib/styles';
import User2 from '../Icon/components/User2';

const PlaceholderIcon = styled(User2)`
    width: 80%;
    height: 80%;
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
`;

interface Props {
    size: AvatarSize | [AvatarSize, AvatarSize, AvatarSize, AvatarSize];
}

type AvatarSize = 'extra-small' | 'small' | 'medium' | 'large' | 'extra-large';

const Placeholder = styled.span<{ backgroundColor: string; sizes: AvatarSizing[] }>(
    ({ sizes, backgroundColor }) => {
        return css`
            display: inline-block;
            vertical-align: middle;
            background: repeating-linear-gradient(
                ${backgroundColor},
                ${backgroundColor} 83.9%,
                white 84%,
                white 100%
            );
            animation-name: ${svgColorFadeIn(backgroundColor)};
            animation-fill-mode: forwards;
            animation-duration: 0.8s;
            position: relative;
            color: ${colors.white};
            border-radius: 50%;
            border: 3px solid ${colors.white};
            border-bottom-color: ${colors.white};
            box-shadow: ${boxShadows.low};
            overflow: hidden;

            @media ${media('small')} {
                width: ${sizes[0].avatarSize}px;
                height: ${sizes[0].avatarSize}px;
            }

            @media ${media('medium')} {
                width: ${sizes[1].avatarSize}px;
                height: ${sizes[1].avatarSize}px;
            }

            @media ${media('normal')} {
                width: ${sizes[2].avatarSize}px;
                height: ${sizes[2].avatarSize}px;
            }

            @media ${media('large')} {
                width: ${sizes[3].avatarSize}px;
                height: ${sizes[3].avatarSize}px;
            }
        `;
    }
);

function getRandomColour(): string {
    const colorList = colors.vivid;
    const keys: (keyof typeof colorList)[] = Object.keys(colorList) as (keyof typeof colorList)[];
    const randomColor = colorList[keys[(keys.length * Math.random()) << 0]];
    return randomColor;
}

function getResponsiveAvatarPxSizes(
    size: AvatarSize | [AvatarSize, AvatarSize, AvatarSize, AvatarSize]
): AvatarSizing[] {
    if (Array.isArray(size)) {
        return size.map((s) => avatars.sizes[s as AvatarSize]);
    } else {
        const s = avatars.sizes[size as AvatarSize];
        return [s, s, s, s];
    }
}

export default function AvatarPlaceholder({ size }: Props) {
    const [color, setColor] = useState(colors.grey300);

    useEffect(() => {
        setColor(getRandomColour());
    }, []);

    return (
        <Placeholder backgroundColor={color} sizes={getResponsiveAvatarPxSizes(size)}>
            <PlaceholderIcon />
        </Placeholder>
    );
}
